<template>
  <div
    class="relative flex items-center max-w-[8rem] border border-gray-300 rounded-lg shadow-lg shadow-gray-100 h-8 overflow-hidden"
  >
    <button
      id="decrement-button"
      type="button"
      data-input-counter-decrement="quantity-input"
      class="px-3 h-full outline-0"
      :disabled="disabled || value === 0 || disableMinus"
      @click="handleDecrease"
    >
      <Minus
        class="w-3 h-3 text-gray-900"
        :class="{ '!text-gray-400': disabled || value === 0 || disableMinus }"
      />
    </button>
    <input
      id="quantity-input"
      v-model="internalValue"
      type="text"
      data-input-counter
      aria-describedby="helper-text-explanation"
      class="disabled:bg-white disabled:text-gray-400 border-gray-300 text-center text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5"
      placeholder="999"
      :disabled="disabled"
      required
      :readonly="isReadonly"
      @change="
        handleChangeInputValue(($event.target as HTMLInputElement)?.value ?? '')
      "
    />

    <button
      id="increment-button"
      type="button"
      data-input-counter-increment="quantity-input"
      class="px-3 h-full outline-none"
      :disabled="disabled || disablePlus"
      @click="handleIncrease"
    >
      <Plus
        class="w-3 h-3 text-gray-900"
        :class="{ '!text-gray-400': disabled }"
      />
    </button>
  </div>
</template>

<script lang="ts" setup>
import Plus from '~/public/icons/plus.svg'
import Minus from '~/public/icons/minus.svg'

const props = defineProps<{
  max?: number
  min?: number
  disableMinus?: boolean
  disablePlus?: boolean
  disabled?: boolean
  isReadonly?: boolean
  isDatesLoaded?: boolean
}>()

const value = defineModel<number>({
  default: 0,
})

const emit = defineEmits(['maxReached', 'minReached', 'change'])

const internalValue = ref(value.value)

const updateQuantityFromSession = () => {
  if (!process.client) return
  if (!sessionStorage.getItem('filters')) return

  const quantityFromSession = JSON.parse(sessionStorage.getItem('filters'))

  if (quantityFromSession?.peopleAmount) {
    internalValue.value = Number(quantityFromSession.peopleAmount)
    emit('change', value.value)
    delete quantityFromSession.peopleAmount
    sessionStorage.setItem('filters', JSON.stringify(quantityFromSession))
  }
}

const handleIncrease = () => {
  handleChangeInputValue(Number(internalValue.value) + 1)
  emit('change', value.value)
}

const handleDecrease = () => {
  handleChangeInputValue(Number(internalValue.value) - 1)
  emit('change', value.value)
}

const handleChangeInputValue = (newVal: string | number) => {
  // Convert input to number and handle invalid input
  const numericValue = Number(newVal)
  if (isNaN(numericValue)) {
    internalValue.value = props.min ?? 0
    value.value = internalValue.value
    emit('change', value.value)
    return
  }

  // Apply min/max constraints
  let finalValue = numericValue
  if (props.max && finalValue > props.max) {
    finalValue = props.max
    emit('maxReached')
  }
  if (props.min && finalValue < props.min) {
    finalValue = props.min
    emit('minReached')
  }

  // Update both internal and model values
  internalValue.value = finalValue
  value.value = finalValue
  emit('change', finalValue)
}

watch(
  () => props.min,
  () => {
    handleChangeInputValue(internalValue.value)
  }
)

watch(
  () => props.max,
  () => {
    handleChangeInputValue(internalValue.value)
  }
)

watch(
  () => props.isDatesLoaded,
  () => {
    updateQuantityFromSession()
  }
)
</script>
